import { FlexBox } from '@dixa/lib-shared-habla';
import type {
  ConversationMessageDeliveryEvent,
  ConversationMessageDeliveryEventStatus,
} from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import groupBy from 'lodash/groupBy';
import { ReactNode, useMemo } from 'react';

import { getConversationMessageDeliveryStatusAttributes } from 'utils/conversation';

type ConversationMessageDeliveryReportProps = PropsWithClassName & {
  /** The computed delivery status. */
  computedStatus: ConversationMessageDeliveryEventStatus;
  /** The extended conversation message delivery events. */
  deliveryEvents: readonly ConversationMessageDeliveryEvent[];
};

/** Represents the component for displaying a list of delivery events. */
export const ConversationMessageDeliveryReport = ({
  className,
  computedStatus,
  deliveryEvents,
}: ConversationMessageDeliveryReportProps): JSX.Element => {
  const deliveries = useMemo(() => {
    const contactPointDeliveries = Object.entries(
      groupBy(deliveryEvents, 'contact_point_value'),
    );
    const isReadAtLeastByOne =
      contactPointDeliveries.length > 1 && computedStatus === 'seen';

    return isReadAtLeastByOne ? (
      <DeliveryStatusMessage
        icon={getConversationMessageDeliveryStatusAttributes('seen').icon}
        label="Read at least by one of the recipients"
      />
    ) : (
      contactPointDeliveries.map(([key, events]) => {
        const { icon, labelTemplate } =
          getConversationMessageDeliveryStatusAttributes(events[0].event_type);
        const label =
          typeof labelTemplate === 'string'
            ? labelTemplate
            : labelTemplate({
                info: events[0].info ? `: ${events[0].info}` : '',
              });

        return <DeliveryStatusMessage key={key} icon={icon} label={label} />;
      })
    );
  }, [computedStatus, deliveryEvents]);

  return (
    <FlexBox className={className} gap="100" flexDirection="column">
      {deliveries}
    </FlexBox>
  );
};

type DeliveryStatusProps = {
  icon: ReactNode;
  label: ReactNode;
};

const DeliveryStatusMessage = ({
  icon,
  label,
}: DeliveryStatusProps): JSX.Element => {
  return (
    <FlexBox gap="200" alignItems="center">
      {icon}
      {label}
    </FlexBox>
  );
};
